import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { LockIcon } from '@blocksuite/icons/lit';
import { css } from '@emotion/css';
import { html, type TemplateResult } from 'lit';

import { renderUniLit } from '../../utils/uni-component/uni-component.js';
import type { Property } from '../../view-manager/property.js';

/**
 * Shared column-header markup used by every data-view preset. The
 * table view pioneered the look — a flex row with a type-icon chip
 * on the left, the property name on the right, 6 px padding, and a
 * subtle hover background. Gantt reuses exactly the same structure
 * for its label-column extra headers so the two views line up
 * pixel-for-pixel; any future preset (kanban board header, detail
 * view, ...) can call `renderColumnHeader` to inherit the look.
 *
 * Keeping the markup and the CSS in one place means a future tweak
 * to padding, icon sizing, or hover colour lands in every preset at
 * once — no cross-view drift.
 */

export const columnHeaderContentStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  width: '100%',
  height: '100%',
  padding: '6px',
  boxSizing: 'border-box',
  position: 'relative',
  cursor: 'pointer',

  '&:hover': {
    background: 'var(--streamlain-hover-color)',
  },
});

export const columnHeaderTextRowStyle = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  overflow: 'hidden',
  color: 'var(--streamlain-text-secondary-color)',
  fontSize: '14px',
  position: 'relative',
  minWidth: 0,
});

export const columnHeaderTypeIconStyle = css({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '4px',
  padding: '2px',
  fontSize: '18px',
  color: unsafeCSSVarV2('database/textSecondary').cssText,
  flexShrink: 0,
});

export const columnHeaderTextContentStyle = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  minWidth: 0,
});

export const columnHeaderNameStyle = css({
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontWeight: 500,
  minWidth: 0,
});

// Small semi-transparent lock next to a property name to signal that
// the column is read-only (system columns: created_at, updated_at,
// created_by, updated_by). Shared across table + gantt column headers
// via `renderColumnHeader`; kanban cell icon reuses `readonlyLockStyle`
// directly for its inline rendering.
export const readonlyLockStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: '14px',
  opacity: 0.45,
  color: 'var(--streamlain-icon-color)',

  '& svg': {
    width: '12px',
    height: '12px',
  },
});

export function renderReadonlyLock(property: Property): TemplateResult | null {
  if (!property.readonly$.value) return null;
  return html`<span
    class=${readonlyLockStyle}
    title="Read-only — value set by the system"
    aria-label="Read-only column"
    >${LockIcon()}</span
  >`;
}

export type RenderColumnHeaderOptions = {
  onClick?: (e: MouseEvent) => void;
  onContextMenu?: (e: MouseEvent) => void;
  onTypeIconClick?: (e: MouseEvent) => void;
  extraSlot?: TemplateResult | typeof html;
  /**
   * Disable hover / cursor affordances for views that aren't wiring
   * a click action — otherwise the shared styles imply an
   * interaction that does nothing.
   */
  readonly?: boolean;
};

export function renderColumnHeader(
  property: Property,
  options: RenderColumnHeaderOptions = {}
): TemplateResult {
  const { onClick, onContextMenu, onTypeIconClick, extraSlot, readonly } =
    options;
  const icon = property.icon;
  return html`<div
    class=${columnHeaderContentStyle}
    style=${readonly ? 'cursor: default;' : ''}
    @click=${onClick}
    @contextmenu=${onContextMenu}
  >
    <div class=${columnHeaderTextRowStyle}>
      ${icon
        ? html`<div
            class=${columnHeaderTypeIconStyle}
            @click=${onTypeIconClick}
          >
            ${renderUniLit(icon)}
          </div>`
        : null}
      <div class=${columnHeaderTextContentStyle}>
        <span class=${columnHeaderNameStyle}>${property.name$.value}</span>
        ${renderReadonlyLock(property)}
      </div>
    </div>
    ${extraSlot ?? null}
  </div>`;
}
